import { useNavigate, useParams } from 'react-router-dom';
import FoldersHeader from './FoldersHeader';
import Card from '@salesforce/design-system-react/components/card';
import {
  useProjectServiceListProjectsQuery,
  FactoryEmaillabApiV1Folder,
  useFolderServiceListFoldersQuery,
  useCampaignServiceSyncCampaignMutation,
  useSfmcFolderServiceListSfmcFoldersQuery,
  FactoryEmaillabApiV1Campaign
} from '../../app/apiGenerated';
import { useEffect, useState } from 'react';
import { BreadCrumb } from '@salesforce/design-system-react';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Illustration from '@salesforce/design-system-react/components/illustration';
import FoldersList, {
  CampaignObjectDetails,
  FolderObjectDetails
} from './FoldersList';
import Icon from '@salesforce/design-system-react/components/icon';
import Button from '@salesforce/design-system-react/components/button';
import Modal from '@salesforce/design-system-react/components/modal';
import Tree from '@salesforce/design-system-react/components/tree';

interface ObjectHomeProps {
  filter?: string;
}

const FoldersHome = ({ filter }: ObjectHomeProps) => {
  // const [filterData, setfilterData] = useState(filter);
  const navigate = useNavigate();
  const urlParams = useParams();

  const [foldersModalIsOpen, setFoldersModalIsOpen] = useState(false);
  // const [sfmcFolders, setFoldersModalIsOpen] = useState(false);

  const [foldersTree, setFoldersTree] = useState<{
    selectedNode: any;
    selectedCampaign: FactoryEmaillabApiV1Campaign | undefined;
    nodes: Record<string, any>;
  }>({
    selectedNode: undefined,
    selectedCampaign: undefined,
    nodes: {}
  });

  // console.log(urlParams);
  const projectId = urlParams?.['id'];
  const folderPathArray =
    urlParams && urlParams['*'] && urlParams['*'] !== ''
      ? urlParams['*']?.split('/')
      : [];

  const sfmcFoldersQuery = useSfmcFolderServiceListSfmcFoldersQuery(
    {},
    {
      // pollingInterval: 3000,
      // refetchOnMountOrArgChange: true,
      skip: false
    }
  );

  const projectsQuery = useProjectServiceListProjectsQuery(
    {},
    {
      // pollingInterval: 3000,
      // refetchOnMountOrArgChange: true,
      skip: false
    }
  );

  const foldersQuery = useFolderServiceListFoldersQuery(
    { filter: `${projectId}` },
    {
      // pollingInterval: 3000,
      // refetchOnMountOrArgChange: true,
      skip: projectId === undefined
    }
  );

  const [
    campaignServiceSyncCampaignSection, // This is the mutation trigger
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { isLoading: isSyncing } // This is the destructured mutation result
  ] = useCampaignServiceSyncCampaignMutation();

  const selectedProject =
    projectId && projectsQuery && projectsQuery.data?.projects
      ? projectsQuery.data.projects.find((p) => p.name === projectId)
      : undefined;

  const separators = foldersQuery.data?.folders || [];

  const getNestedFolder = (
    names: string[],
    folder: FactoryEmaillabApiV1Folder | undefined,
    displayNames: string[]
  ): [FactoryEmaillabApiV1Folder | undefined, string[]] => {
    const found = folder?.folders?.find((f) => f.name === names[0]);
    // const b = a?.folders?.find((f) => f.name === names[0]);

    if (names.length <= 1) {
      // console.log(folder, names, found);
      return [found, [...displayNames, found?.display_name || '']];
    }
    // console.log(folder, names, found);
    return found
      ? getNestedFolder([...names].splice(1, 9e9), found, [
          ...displayNames,
          found?.display_name || ''
        ])
      : [undefined, displayNames];
  };

  const getFolderFromSeparators = (
    names: string[],
    folders: FactoryEmaillabApiV1Folder[],
    displayNames: string[]
  ): [FactoryEmaillabApiV1Folder | undefined, string[]] => {
    if (names.length === 0) {
      return [undefined, []];
    }

    if (names.length === 1) {
      const found = folders.find((f) => f.name === names[0]);
      return found ? [found, [found.display_name || '']] : [undefined, []];
    }

    const res = folders
      .map((f) =>
        getNestedFolder([...names].splice(1, 9e9), f, [f.display_name])
      )
      .filter(([f, displayNames]) => f !== undefined);
    return res.length > 0 ? res[0] : [undefined, displayNames];
  };

  const [currentFolder, displayNames] = getFolderFromSeparators(
    folderPathArray,
    separators,
    []
  );

  const onClickCampaign = (campaign: FactoryEmaillabApiV1Campaign) => {
    setFoldersModalIsOpen(true);
    setFoldersTree((prev) => ({ ...prev, selectedCampaign: campaign }));
  };

  // console.log(sfmcFoldersQuery.data);

  const sfmcFolders =
    sfmcFoldersQuery.data?.sfmc_folder
      ?.map((folder, index) => {
        const childFoldersIds =
          sfmcFoldersQuery.data?.sfmc_folder
            ?.filter((f) => f.parent_id === folder.id)
            .map((f) => f.id) || [];
        return {
          label: folder.display_name,
          type: childFoldersIds.length > 0 ? 'branch' : 'item',
          nodes: childFoldersIds,
          id: folder.id
        };
      })
      .reduce((accumulator, folder) => {
        return { ...accumulator, [folder.id || 0]: folder };
      }, {}) || {};

  useEffect(() => {
    setFoldersTree((prev) => ({ ...prev, nodes: sfmcFolders }));
  }, [sfmcFoldersQuery.isLoading, sfmcFoldersQuery.isFetching]);

  const rootNode = Object.values(foldersTree.nodes).find(
    (f) => f.parent_id === undefined
  )?.id;

  // console.log(foldersTree);
  const handleExpand = (data: any) => {
    setFoldersTree((previousData) => {
      const isExpanded =
        previousData.nodes[data.node.id]['expanded'] !== undefined
          ? !previousData.nodes[data.node.id]['expanded']
          : true;
      return {
        ...previousData,
        nodes: {
          ...previousData.nodes,
          [data.node.id]: {
            ...previousData.nodes[data.node.id],
            expanded: isExpanded
          }
        }
      };
    });
  };

  const handleClick = (data: any) => {
    setFoldersTree((prevState) => {
      const selectedNode = prevState.selectedNode
        ? {
            [prevState.selectedNode.id]: {
              ...prevState.nodes[prevState.selectedNode.id],
              selected: false
            }
          }
        : {};
      return {
        ...prevState,
        nodes: {
          ...prevState.nodes,
          ...{
            [data.node.id]: { ...data.node, selected: data.select },
            ...selectedNode
          }
        },
        selectedNode:
          data.node?.id === prevState.selectedNode?.id ? undefined : data.node
      };
    });
  };
  return (
    <div
      id="folders-header"
      className="slds-grid slds-grid_vertical flex-grow-1 align-items-stretch slds-template_bottom-magnet"
      // style={{ height: 'calc(100vh - 50px)', marginBottom: '50px' }}
      style={{ height: 'calc(100vh)' }}
    >
      <FoldersHeader
        title={selectedProject?.display_name || ''}
        selectedProject={selectedProject}
        path={
          <strong>
            <BreadCrumb
              assistiveText={{ label: 'Dartagnan Connector' }}
              trail={[
                <a onClick={() => navigate('')}>
                  {selectedProject?.display_name}
                </a>,
                ...displayNames.map((n, index) => {
                  return (
                    <a
                      key={n}
                      className={
                        index === displayNames.length - 1
                          ? 'slds-text-link_reset cursor-default'
                          : ''
                      }
                      onClick={() =>
                        navigate(
                          [...folderPathArray].slice(0, index + 1).join('/')
                        )
                      }
                    >
                      {n}
                    </a>
                  );
                })
              ]}
            />
          </strong>
        }
      />
      <Card
        id="FolderCard"
        hasNoHeader
        heading=""
        className="flex-grow-1 slds-grid_vertical slds-grid  min-height-0 slds-m-top_small"
        bodyClassName="slds-grid slds-grid_vertical slds-m-bottom_none custom-scroll-overflow"
        style={{
          borderBottom: 'none',
          borderRadius: '0.25rem 0.25rem 0 0'
        }}
      >
        <div
          className="slds-grid flex-grow-1 min-height-0 min-width-0"
          style={{ borderTop: '1px solid rgb(224, 229, 238)' }}
        >
          <div
            className="slds-col"
            style={{
              background: '#f3f2f2'
            }}
          >
            {foldersQuery.isLoading || foldersQuery.isFetching ? (
              <div
                style={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                  borderLeft: '1px solid rgb(224, 229, 238)'
                }}
              >
                <Spinner
                  size="medium"
                  variant="brand"
                  assistiveText={{ label: 'Main Frame Loading...' }}
                />
              </div>
            ) : (
              <>
                {separators === undefined ||
                (separators !== undefined && separators.length == 0) ? (
                  <Illustration
                    heading="It looks like there is no data yet"
                    messageBody="Let's get started by creating a new entry and building the list together."
                    name="No Access"
                    // path="/assets/images/illustrations/empty-state-no-results.svg#no-results"
                    path="/assets/images/illustrations/no-data.svg#no-data"
                    size="small"
                    internalIllustration={true} // silenceDeprecationWarning
                    className="height-100 slds-grid slds-grid_vertical slds-grid slds-grid_align-center slds-grid_vertical-align-center width-100"
                  />
                ) : separators !== undefined && separators.length > 0 ? (
                  <div
                    className="slds-grid  overflow-hidden flex-grow-1"
                    style={{ maxHeight: '100%' }}
                  >
                    {folderPathArray.length === 0 ? (
                      <FoldersList
                        separators={separators}
                        onClickCampaign={(campaign) => {
                          onClickCampaign(campaign);
                        }}
                        onClickFolder={(p) => {
                          // setTreeFolders([...treeFolders, ...p]);
                          navigate(
                            `${p.map((folder) => folder.name).join('/')}`
                          );
                        }}
                      />
                    ) : (
                      <>
                        {currentFolder && (
                          <>
                            <div
                              className="slds-p-around_large slds-m-around_x-small"
                              style={{ overflowY: 'auto' }}
                            >
                              <div className="slds-grid slds-grid_vertical-align-center slds-text-heading_medium slds-m-bottom_medium">
                                <Button
                                  variant="icon"
                                  iconCategory="utility"
                                  iconName="chevronleft"
                                  iconSize="large"
                                  className="slds-m-right_medium"
                                  onClick={() => {
                                    navigate(
                                      `${folderPathArray
                                        .slice(0, folderPathArray.length - 1)
                                        .join('/')}`
                                    );
                                  }}
                                />
                                <Icon
                                  category="standard"
                                  name="folder"
                                  size="medium"
                                />
                                <span className="slds-m-left_small">
                                  <strong>{currentFolder.display_name}</strong>
                                  <span className="slds-text-heading_small">
                                    <span className="slds-m-left_x-small">
                                      <i>
                                        Folders:
                                        {currentFolder.folders?.length || 0}
                                      </i>
                                    </span>
                                    <span className="slds-m-left_x-small">
                                      <i>
                                        Campaigns:
                                        {currentFolder.campaigns?.length || 0}
                                      </i>
                                    </span>
                                  </span>
                                </span>
                              </div>
                              <div className="slds-grid slds-gutters slds-wrap slds-p-around_small">
                                {[...(currentFolder.folders || [])]
                                  ?.sort((a, b) =>
                                    a.display_name?.localeCompare(
                                      b.display_name
                                    )
                                  )
                                  ?.map((folder) => {
                                    return (
                                      <FolderObjectDetails
                                        item={folder}
                                        onClick={(folder) => {
                                          // setTreeFolders([...treeFolders, folder]);
                                          // console.log(folder.name);
                                          navigate(
                                            `${
                                              folderPathArray.join('/') +
                                              '/' +
                                              folder.name
                                            }`
                                          );
                                        }}
                                        width="20rem"
                                      />
                                    );
                                  })}
                                {[...(currentFolder.campaigns || [])]
                                  ?.sort((a, b) =>
                                    a.display_name?.localeCompare(
                                      b.display_name
                                    )
                                  )
                                  ?.map((campaign) => {
                                    return (
                                      <CampaignObjectDetails
                                        item={campaign}
                                        onClick={() => {
                                          onClickCampaign(campaign);
                                          // campaignServiceSyncCampaignSection({
                                          //   factoryEmaillabApiV1SyncCampaignRequest:
                                          //     {
                                          //       campaign:
                                          //         'campaigns/' + campaign.name,
                                          //       name: campaign.display_name
                                          //     }
                                          // })
                                          //   .unwrap()
                                          //   .then((result) => {
                                          //     console.log(result);
                                          //   });
                                        }}
                                        width="20rem"
                                      />
                                    );
                                  })}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ) : null}
              </>
            )}
          </div>
          <Modal
            // isOpen={false}
            isOpen={foldersModalIsOpen}
            onRequestClose={() => setFoldersModalIsOpen(false)}
            dismissOnClickOutside={false}
            heading="Select Destination Folder"
            footer={[
              <Button
                label="Cancel"
                onClick={() => setFoldersModalIsOpen(false)}
              />,
              <Button
                label="Sync Campaign"
                variant="brand"
                disabled={foldersTree.selectedNode === undefined}
                onClick={() => {
                  campaignServiceSyncCampaignSection({
                    factoryEmaillabApiV1SyncCampaignRequest: {
                      campaign:
                        'campaigns/' + foldersTree.selectedCampaign?.name,
                      name: foldersTree.selectedCampaign?.display_name,
                      category_id: foldersTree.selectedNode?.id
                    }
                  })
                    .unwrap()
                    .then((result) => {
                      console.log(result);
                      setFoldersModalIsOpen(false);
                    });
                }}
              />
            ]}
            // size="small"
            // disableClose={true}
            // prompt="info"
          >
            <div className="slds-p-around_medium">
              <Tree
                // assistiveText={this.props.assistiveText}
                // className={this.props.className}
                getNodes={(node) =>
                  node.nodes
                    ? node.nodes.map((id: string) => foldersTree.nodes[id])
                    : []
                }
                heading="Content Builder Folders"
                id="folder-list"
                // listStyle={this.props.listStyle}
                listClassName="slds-p-vertical_medium"
                nodes={rootNode ? [rootNode] : []}
                onExpandClick={(event, data) => handleExpand(data)}
                onClick={(event, data) => handleClick(data)}
                // onScroll={this.props.onScroll || this.handleScroll}
                // searchTerm={this.props.searchTerm || this.state.searchTerm}
              />
              {foldersTree.selectedNode ? (
                <div
                  className="slds-p-around_small"
                  style={{
                    background: '#eef1f1',
                    borderRadius: '5px'
                  }}
                >
                  Selected Folder :{' '}
                  <strong>{foldersTree.selectedNode.label}</strong>
                </div>
              ) : (
                <div
                  className="slds-p-around_small"
                  style={{
                    opacity: 0.6,
                    background: '#eef1f1',
                    borderRadius: '5px'
                  }}
                >
                  No Folder Selected
                </div>
              )}
            </div>
          </Modal>
          <Modal
            // isOpen={false}
            isOpen={isSyncing}
            // size="small"
            disableClose={true}
            prompt="info"
          >
            <div className="slds-p-around_medium">
              <div className="slds-text-align_center slds-p-around_small">
                <Illustration
                  name="Setup"
                  path="/assets/images/illustrations/setup.svg#setup"
                  // size="large"
                  style={{ height: '300px' }}
                  internalIllustration={true} // silenceDeprecationWarning
                />
                <p className="slds-m-top_small slds-text-heading_large">
                  Your campaign is syncing ...
                </p>
                <p className="slds-text-body_small">
                  Please wait, this may take few minutes
                </p>
              </div>
              <div
                style={{
                  position: 'relative',
                  height: '60px'
                }}
                className="slds-m-bottom_medium"
              >
                <Spinner
                  size="medium"
                  variant="brand"
                  assistiveText={{
                    label: 'Main Frame Loading...'
                  }}
                />
              </div>
            </div>
          </Modal>
        </div>
      </Card>
    </div>
  );
};

export default FoldersHome;
